<template>
    <div v-if="hasEvent" :class="`box trend-box ${dialogClass}`">
        <span :class="'trend-pill ' + badgeColorClass">Trend seit</span>

        <div class="trend-body">
            <img class="trend-logo" :src="imgPath" alt="" />
            <div class="trend-text">
                <div class="trend-title">{{ opponentName }}</div>
                <div class="trend-meta">{{ metaLine }}</div>
            </div>
            <span :class="'trend-value ' + badgeColorClass">{{ signedDiff }}</span>
        </div>
    </div>
</template>

<script>
/**
 * Der Stimmungstrend seit einem Ereignis, als Zeile in der schmalen Barometer-Spalte.
 *
 * Aufgebaut wie die Detailzeile unter dem Raster: Wappen, darauf der Gegner mit Spieltag und
 * Ergebnis, rechts der Wert. Die Vorgaengerfassung reihte Wappen, Ergebnis-Etikett und Spieltag
 * nebeneinander in eine Zeile und schob den Wert mit margin-left an den Rand - dazwischen blieb
 * eine leere Flaeche, die nichts trug.
 */
export default {
    props: {
        trend: {
            type: Object,
            required: true
        }
    },
    computed: {
        event() {
            return this.trend?.timeframe?.eventDesc || null;
        },
        hasEvent() {
            return this.event?.text != null;
        },
        isMatch() {
            return this.event?.type === 1;
        },
        trendState() {
            if (this.trend.diff === 0)
                return 'warning';
            return this.trend.direction === 'up' ? 'success' : 'error';
        },
        badgeColorClass() {
            return 'trend-badge-' + this.trendState;
        },
        dialogClass() {
            return this.trendState === 'success' ? 'success-dialog' : this.trendState === 'error' ? 'error-dialog' : 'warning-dialog';
        },
        imgPath() {
            return this.isMatch ? this.event.image : '/chart-imgs/thunder.png';
        },
        resultText() {
            return this.event?.text || '–';
        },
        signedDiff() {
            return (this.trend.diff > 0 ? '+' : '') + this.trend.diff;
        },
        /** Gegnername, sofern die API ihn an diesem Endpunkt liefert - sonst der Spieltag. */
        opponentName() {
            if (!this.isMatch) return this.event?.text || '';
            return this.event?.opp || this.event?.md || 'Spieltag';
        },
        /**
         * Zweite Zeile: Spieltag und Ergebnis, durch Abstand getrennt statt durch ein Zeichen.
         * Der Spieltag entfaellt, wenn er mangels Gegnernamen schon die Titelzeile fuellt.
         */
        metaLine() {
            if (!this.isMatch) return '';

            const parts = [];
            if (this.event?.opp && this.event?.md) parts.push(this.event.md);
            parts.push(this.resultText);
            return parts.join(' ');
        }
    }
}
</script>

<style scoped>
.trend-box {
    position: relative;
    padding: var(--spacing-md);
    padding-top: calc(var(--spacing-md) + 6px);
}

.trend-pill {
    position: absolute;
    top: -12px;
    left: var(--spacing-md);
    display: inline-flex;
    align-items: center;
    font-size: var(--fs-sm);
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 999px;
    box-shadow: var(--shadow-md);
    white-space: nowrap;
}

/* Auf den Signalfarben liegt dunkle Schrift bei 4,4:1 bis 7,7:1, weisse dagegen nur bei 2,2:1
   bis 3,8:1 - deshalb durchgaengig dunkel, wie auf den Kacheln im Raster. */
.trend-badge-success,
.trend-badge-warning,
.trend-badge-error {
    color: var(--chart-surface);
}

.trend-badge-success {
    background: var(--success);
}

.trend-badge-warning {
    background: var(--warning);
}

.trend-badge-error {
    background: var(--error);
}

.trend-body {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
}

.trend-logo {
    width: 34px;
    height: 34px;
    object-fit: contain;
    flex-shrink: 0;
}

/* Nimmt die Breite zwischen Wappen und Wert ein, statt sie leer zu lassen. */
.trend-text {
    flex: 1 1 auto;
    min-width: 0;
}

.trend-title {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.trend-meta {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
}

.trend-value {
    flex-shrink: 0;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: var(--fs-lg);
    font-weight: 700;
}
</style>
